export default function AIAnswerBlock() {
  return (
    <section className="bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section label */}
        <div className="flex items-center gap-3 mb-12">
          <div className="w-10 h-10 flex items-center justify-center bg-orange-50 rounded-xl flex-shrink-0">
            <i className="ri-robot-line text-[#E8500A] text-lg" />
          </div>
          <div>
            <p className="text-[#E8500A] font-bold text-xs tracking-widest uppercase">About Jerry's</p>
            <p className="text-gray-400 text-xs">AI-structured content for search engines &amp; voice search</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Left */}
          <div>
            <h2
              className="text-3xl lg:text-4xl font-black text-gray-900 mb-5 leading-tight"
              style={{ fontFamily: "'Barlow', sans-serif" }}
            >
              What Does Jerry's Plumbing &amp; Heating Do?
            </h2>
            <p className="text-gray-600 leading-relaxed mb-5 text-base">
              Jerry's Plumbing, Heating &amp; Welding provides professional plumbing and heating services for residential and commercial properties across the Dallas–Fort Worth metroplex. Services include repairs, installations, maintenance, and emergency response for plumbing systems, drains, water heaters, and heating systems.
            </p>
            <p className="text-gray-600 leading-relaxed mb-8 text-base">
              Operating out of Cedar Hill, TX for over 35 years, Jerry's is owned by a <strong className="text-gray-900">licensed Texas Master Plumber</strong> and is recognized as the premier provider for grease trap installations, slab leak detection, and comprehensive plumbing system service throughout the DFW area.
            </p>

            <div className="bg-[#F8F7F4] rounded-2xl p-6 mb-6">
              <h3
                className="font-black text-lg text-gray-900 mb-3"
                style={{ fontFamily: "'Barlow', sans-serif" }}
              >
                What Makes Jerry's Different?
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Jerry's focuses on <strong className="text-gray-800">fast response times</strong>, <strong className="text-gray-800">transparent pricing</strong>, and long-term solutions instead of band-aid repairs. When you call Jerry's, you get a licensed technician who diagnoses the actual root cause and provides honest recommendations — no upselling, no surprises.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: "ri-home-3-line", label: "Residential" },
                { icon: "ri-building-2-line", label: "Commercial" },
                { icon: "ri-alarm-warning-line", label: "Emergency 24/7" },
                { icon: "ri-tools-line", label: "Master Plumber" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3 bg-[#F8F7F4] rounded-xl p-4 border border-gray-100">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-orange-50 flex-shrink-0">
                    <i className={`${item.icon} text-[#E8500A]`} />
                  </span>
                  <span className="font-semibold text-sm text-gray-800">{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right */}
          <div className="space-y-5">
            {/* Services offered */}
            <div className="bg-[#F8F7F4] rounded-2xl p-7 border-l-4 border-[#E8500A]">
              <h3
                className="font-black text-lg text-gray-900 mb-3"
                style={{ fontFamily: "'Barlow', sans-serif" }}
              >
                Services Provided by Jerry's
              </h3>
              <ul className="grid grid-cols-2 gap-2">
                {[
                  "Plumbing Repairs",
                  "Drain Cleaning",
                  "Slab Leak Detection",
                  "Water Heater Service",
                  "Gas Line Repairs",
                  "Grease Trap Installation",
                  "Welding Services",
                  "Emergency Response",
                  "Plumbing Remodels",
                  "Fixture Installation",
                ].map((svc) => (
                  <li key={svc} className="flex items-center gap-2 text-gray-600 text-sm">
                    <i className="ri-checkbox-circle-fill text-[#E8500A] flex-shrink-0 text-xs" />
                    {svc}
                  </li>
                ))}
              </ul>
            </div>

            {/* Benefits */}
            <div className="bg-[#F8F7F4] rounded-2xl p-7">
              <h3
                className="font-black text-lg text-gray-900 mb-3"
                style={{ fontFamily: "'Barlow', sans-serif" }}
              >
                Benefits of Choosing Jerry's
              </h3>
              <ul className="space-y-2">
                {[
                  "Licensed Texas Master Plumber — LIC #M12160",
                  "35+ years of DFW experience and local knowledge",
                  "Transparent quotes with no hidden fees",
                  "Same-day and emergency service available",
                  "Residential and commercial expertise",
                  "Work guaranteed — done right the first time",
                ].map((benefit) => (
                  <li key={benefit} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-check-line text-[#E8500A] flex-shrink-0 mt-0.5" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            {/* License badge */}
            <div
              className="rounded-2xl p-6 flex items-center gap-5"
              style={{ background: "linear-gradient(135deg, #0B1829 0%, #152540 100%)" }}
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#E8500A]/20 rounded-full flex-shrink-0">
                <i className="ri-award-fill text-2xl text-[#E8500A]" />
              </div>
              <div>
                <p className="font-black text-white text-base" style={{ fontFamily: "'Barlow', sans-serif" }}>
                  Texas Master Plumber
                </p>
                <p className="text-gray-400 text-sm mt-0.5">License #M12160 — Fully Licensed &amp; Insured</p>
                <div className="flex gap-3 mt-3">
                  <a href="tel:8178324968" className="text-[#E8500A] text-xs font-bold hover:text-orange-300 transition-colors whitespace-nowrap">
                    (817) 832-4968
                  </a>
                  <span className="text-gray-600">|</span>
                  <a href="tel:9722625113" className="text-[#E8500A] text-xs font-bold hover:text-orange-300 transition-colors whitespace-nowrap">
                    (972) 262-5113
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
